import { useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import LeadDetailModal from "@/components/LeadDetailModal";
import { useAccountsData } from "./accounts/useAccountsData";
import { filterItems, emptyForm, isBlankAccount } from "./accounts/accountsModel";
import { AccountDetail } from "./accounts/AccountDetail";
import { AccountsTable, LeadsTable } from "./accounts/AccountsTables";
import { AccountsHeader, ViewToggle, AccountsFilters } from "./accounts/AccountsToolbar";
import { AddItemModal, DeleteConfirmModal } from "./accounts/AccountsModals";
import { IntakeModal } from "./intake/IntakePanel";
import { KnowledgeSummaryBar } from "./accounts/KnowledgeSummaryBar";

const AccountsView = () => {
  const { userId } = useParams();
  const data = useAccountsData(userId);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [relationship, setRelationship] = useState("all");
  const [viewMode, setViewMode] = useState("accounts");
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [addForm, setAddForm] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [intakeOpen, setIntakeOpen] = useState(false);

  const rows = useMemo(
    () => filterItems(viewMode === "accounts" ? data.accounts : data.leads, search, status, relationship),
    [viewMode, data.accounts, data.leads, search, status, relationship]
  );

  const submitAdd = async () => {
    if (await data.addItem(viewMode, addForm)) setAddForm(null);
  };

  const confirmDelete = async () => {
    if (!(await data.deleteItem(viewMode, deleteTarget))) return;
    if (selectedAccount?.accountId === deleteTarget.accountId) setSelectedAccount(null);
    setDeleteTarget(null);
  };

  const handleLeadUpdated = (updated) => {
    data.updateLead(updated);
    setSelectedLead(updated);
  };

  const refresh = data.refresh;
  const handleAccountChanged = useCallback((patch) => {
    setSelectedAccount((prev) => (prev ? { ...prev, ...patch } : prev));
    refresh();
  }, [refresh]);

  if (data.loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="accounts-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-ism-teal/50" />
      </div>
    );
  }

  const modals = (
    <>
      <AddItemModal form={addForm} viewMode={viewMode} submitting={data.submitting} setForm={setAddForm} onClose={() => setAddForm(null)} onSubmit={submitAdd} />
      <DeleteConfirmModal item={deleteTarget} viewMode={viewMode} submitting={data.submitting} onClose={() => setDeleteTarget(null)} onConfirm={confirmDelete} />
    </>
  );

  if (selectedAccount) {
    return (
      <>
        <AccountDetail account={selectedAccount} onBack={() => setSelectedAccount(null)} onDelete={() => setDeleteTarget(selectedAccount)} onChanged={handleAccountChanged} />
        {modals}
      </>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="accounts-view">
      <AccountsHeader
        viewMode={viewMode}
        hasBlank={data.accounts.some(isBlankAccount)}
        cleaningUp={data.cleaningUp}
        onCleanup={data.cleanupBlank}
        onAdd={() => setAddForm(emptyForm(viewMode))}
        onIntake={() => setIntakeOpen(true)}
        onOutreach={() => toast.info("Outreach action initiated for selected accounts")}
      />
      <ViewToggle viewMode={viewMode} setViewMode={setViewMode} accountsCount={data.accounts.length} leadsCount={data.leads.length} />
      <KnowledgeSummaryBar userId={userId} onEnriched={data.refresh} />
      <AccountsFilters viewMode={viewMode} search={search} setSearch={setSearch} status={status} setStatus={setStatus} relationship={relationship} setRelationship={setRelationship} />

      <Card className="glass-card rounded-xl overflow-hidden">
        <ScrollArea className="h-[600px]">
          {viewMode === "accounts"
            ? <AccountsTable rows={rows} onView={setSelectedAccount} onDelete={setDeleteTarget} onAdd={() => setAddForm(emptyForm("accounts"))} />
            : <LeadsTable rows={rows} onView={setSelectedLead} onDelete={setDeleteTarget} onAdd={() => setAddForm(emptyForm("leads"))} />}
        </ScrollArea>
      </Card>

      {modals}
      <IntakeModal open={intakeOpen} onClose={() => setIntakeOpen(false)} userId={userId} onCommitted={data.refresh} />

      <LeadDetailModal isOpen={!!selectedLead} onClose={() => setSelectedLead(null)} lead={selectedLead} userId={userId} onLeadUpdated={handleLeadUpdated} />
    </motion.div>
  );
};

export default AccountsView;
